import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { Fragment, useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { useVisualViewportHeight } from '@/@bothrs-ui/web/hooks'

import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'

// ================================ Constants ================================

const DEFAULT_MIN_CHARS = 3

// ================================ Utilities ================================

/** Splits `text` into ordered segments around every case-insensitive match of `query`. */
export function highlightMatch(text: string, query: string): { text: string; isMatch: boolean }[] {
  if (!query) return [{ text, isMatch: false }]
  const lowerQuery = query.toLowerCase()
  // Escape regex metacharacters so the query is matched literally.
  const escaped = query.replaceAll(/[$()*+.?[\\\]^{|}]/g, '\\$&')
  // The capturing group keeps the matched substrings in the split result,
  // yielding alternating non-match/match segments with original casing.
  const segments = text
    .split(new RegExp(`(${escaped})`, 'gi'))
    .filter(Boolean)
    .map((part) => ({ text: part, isMatch: part.toLowerCase() === lowerQuery }))
  // `text === ''` splits to a single empty string that `filter` drops; fall
  // back to one segment so callers always receive a non-empty array.
  return segments.length > 0 ? segments : [{ text, isMatch: false }]
}

// ================================ Types ================================

export type SearchComboboxProps<T> = {
  id: string
  label: string
  placeholder: string
  selectedName: string | null
  results: T[]
  getResultName: (result: T) => string
  freeTextLabel: (query: string) => ReactNode
  onSelect: (result: T) => void
  onSelectFreeText: (name: string) => void
  onClear: () => void
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  minChars?: number
}

// ================================ Component ================================

/**
 * Generic autocomplete combobox with keyboard navigation, free-text
 * fallback, and ARIA combobox pattern. Domain-agnostic — receives
 * search results, display callbacks, and labels via props.
 */
export const SearchCombobox = <T,>({
  id,
  label,
  placeholder,
  selectedName,
  results,
  getResultName,
  freeTextLabel,
  onSelect,
  onSelectFreeText,
  onClear,
  searchQuery,
  onSearchQueryChange,
  minChars = DEFAULT_MIN_CHARS,
}: SearchComboboxProps<T>) => {
  const theme = useTheme()
  const [activeIndex, setActiveIndex] = useState(-1)
  const searchRef = useRef<HTMLInputElement>(null)
  const previousSelectedRef = useRef<string | null>(null)

  const viewportHeight = useVisualViewportHeight()

  const showDropdown = !selectedName && searchQuery.trim().length >= minChars

  // Auto-reset and refocus when selection is cleared from outside
  useEffect(() => {
    if (previousSelectedRef.current !== null && selectedName === null) {
      onSearchQueryChange('')
      requestAnimationFrame(() => searchRef.current?.focus())
    }
    previousSelectedRef.current = selectedName
  }, [selectedName, onSearchQueryChange])

  useEffect(() => {
    setActiveIndex(-1)
  }, [searchQuery])

  // Keep the active item visible when navigating by keyboard inside the
  // scrollable results container.
  useEffect(() => {
    if (activeIndex < 0) return
    document.querySelector(`#${CSS.escape(`${id}-option-${activeIndex}`)}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, id])

  // -- Handlers --

  const handleSelect = (result: T) => {
    onSelect(result)
    onSearchQueryChange('')
    setActiveIndex(-1)
  }

  const handleAddFreeText = (query: string) => {
    onSelectFreeText(query)
    onSearchQueryChange('')
    setActiveIndex(-1)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return
    const totalOptions = results.length + 1
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, totalOptions - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      if (activeIndex < results.length) {
        handleSelect(results[activeIndex])
      } else {
        handleAddFreeText(searchQuery.trim())
      }
      setActiveIndex(-1)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onSearchQueryChange('')
      setActiveIndex(-1)
    }
  }

  // -- Render --

  return (
    <StRoot style={{ '--visual-vh': `${viewportHeight}px` } as CSSProperties}>
      <Span
        id={`${id}-label`}
        type='BodyLabel'
        color={theme.Colors.Neutrals.Darkest}
        style={{ textTransform: 'uppercase' }}
      >
        {label}
      </Span>
      <StInputRow>
        <StSearchInput
          ref={searchRef}
          type='text'
          data-1p-ignore
          autoComplete='off'
          role='combobox'
          aria-expanded={showDropdown}
          aria-controls={`${id}-listbox`}
          aria-autocomplete='list'
          aria-labelledby={`${id}-label`}
          aria-activedescendant={showDropdown && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
          readOnly={!!selectedName}
          value={selectedName ?? searchQuery}
          placeholder={placeholder}
          onChange={(event_) => {
            if (!selectedName) {
              onSearchQueryChange(event_.target.value)
            }
          }}
          onKeyDown={handleKeyDown}
        />
        {selectedName ? (
          <StClearButton
            type='button'
            onClick={onClear}
            aria-label={t({
              id: 'search.combobox.clearButton',
              message: 'Selectie wissen',
            })}
          >
            <FontAwesomeIcon icon={['fal', 'times-circle']} />
          </StClearButton>
        ) : (
          <StSearchIcon>
            <FontAwesomeIcon icon={['fal', 'magnifying-glass']} />
          </StSearchIcon>
        )}
      </StInputRow>
      <StLiveRegion role='status' aria-live='polite' aria-atomic='true'>
        {showDropdown ? t`${results.length} resultaten gevonden` : ''}
      </StLiveRegion>
      {showDropdown && (
        <StDropdown id={`${id}-listbox`} role='listbox'>
          <StScrollableResults>
            {results.map((result, index) => {
              const name = getResultName(result)
              const segments = highlightMatch(name, searchQuery)
              return (
                <StResultItem
                  key={`${name}-${index}`}
                  id={`${id}-option-${index}`}
                  role='option'
                  aria-selected={index === activeIndex}
                  data-active={index === activeIndex}
                  onClick={() => handleSelect(result)}
                >
                  <Span type='BodyMediumRegular'>
                    {segments.map((segment, segmentIndex) =>
                      segment.isMatch ? (
                        <Span key={segmentIndex} type='BodyMediumBold'>
                          {segment.text}
                        </Span>
                      ) : (
                        <Fragment key={segmentIndex}>{segment.text}</Fragment>
                      ),
                    )}
                  </Span>
                </StResultItem>
              )
            })}
          </StScrollableResults>
          <StDivider />
          <StResultItem
            id={`${id}-option-${results.length}`}
            role='option'
            aria-selected={results.length === activeIndex}
            data-active={results.length === activeIndex}
            onClick={() => handleAddFreeText(searchQuery.trim())}
          >
            <Span type='BodyMediumRegular'>{freeTextLabel(searchQuery)}</Span>
            <StAddIcon>
              <FontAwesomeIcon icon={['fal', 'plus']} />
            </StAddIcon>
          </StResultItem>
        </StDropdown>
      )}
    </StRoot>
  )
}

// ================================ Styled Components ================================

const StRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StInputRow = styled.div`
  position: relative;
`

const StSearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  padding-right: ${({ theme }) => theme.UI.SpacingPx.Space10};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  font-family: DINOT, sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  }

  &:focus {
    border-color: ${({ theme }) => theme.Colors.Theme.Primary};
  }
`

const StSearchIcon = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.UI.SpacingPx.Space11};
  min-height: ${({ theme }) => theme.UI.SpacingPx.Space11};
  font-size: 16px;
`

const StClearButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.UI.SpacingPx.Space11};
  min-height: ${({ theme }) => theme.UI.SpacingPx.Space11};
  font-size: 16px;
`

const StLiveRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const StDropdown = styled.div`
  background: ${({ theme }) => theme.Colors.Background.White};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space1};
`

const StScrollableResults = styled.div`
  overflow-y: auto;
  /* --visual-vh is set from JS (visualViewport.height) because iOS Capacitor
     does not resize the WebView when the keyboard opens, so dvh/vh stay full-size.
     45% keeps the list usable above the keyboard; 15rem caps it when closed. */
  max-height: min(15rem, calc(var(--visual-vh, 100vh) * 0.45));
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.Colors.Neutrals.Light};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const StResultItem = styled.button`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  background: none;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Lighter};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &[data-active='true'] {
    background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
`

const StAddIcon = styled.span`
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const StDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
`
